import { apiPost } from './api'
import { UPLOAD_SIGN } from './endpoints'
import { loadAuth } from './authStorage'

type SignResponse = {
  signature: string
  timestamp: number
  apiKey: string
  cloudName: string
  folder: string
  publicId: string
}

type CloudinaryUploadResult = {
  secureUrl: string
}

/**
 * Uploads a file directly to Cloudinary using a server-signed request.
 *
 * The server signs the upload with a deterministic public_id (`user-{uuid}`)
 * plus `overwrite=true` and `invalidate=true`, so repeated uploads replace the
 * existing asset in place and purge the CDN cache instead of creating
 * orphaned siblings under random IDs.
 *
 * @param file  The image File object from a file input
 * @param folder  "profile-avatars" | "business-logos"
 */
export async function uploadToCloudinary(
  file: File,
  folder: 'profile-avatars' | 'business-logos'
): Promise<CloudinaryUploadResult> {
  const auth = loadAuth()
  if (!auth?.accessToken) throw new Error('Not authenticated')

  const sign = await apiPost<SignResponse, { folder: string }>(
    UPLOAD_SIGN,
    { folder },
  )

  const formData = new FormData()
  formData.append('file', file)
  formData.append('api_key', sign.apiKey)
  formData.append('timestamp', String(sign.timestamp))
  formData.append('signature', sign.signature)
  formData.append('folder', sign.folder)
  // Must match the values the server included in the signature, in the same
  // alphabetical order: folder, invalidate, overwrite, public_id, timestamp.
  formData.append('public_id', sign.publicId)
  formData.append('overwrite', 'true')
  formData.append('invalidate', 'true')

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${sign.cloudName}/image/upload`,
    { method: 'POST', body: formData }
  )

  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: { message?: string } }
    throw new Error(err.error?.message ?? 'Cloudinary upload failed')
  }

  const data = await res.json() as { secure_url: string }
  return { secureUrl: data.secure_url }
}
